import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, CheckCircle, Smartphone, CreditCard, Landmark, Info } from 'lucide-react';
import axios from 'axios';

export default function RazorpayCheckoutPage({ token, setUser }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const tier = searchParams.get('tier') || 'pro';
  const billing = searchParams.get('billing') || 'monthly';

  const [orderId, setOrderId] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('upi'); // 'upi' | 'card' | 'netbanking'

  // Form states
  const [upiId, setUpiId] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [selectedBank, setSelectedBank] = useState('');

  const plans = {
    starter: { name: 'Identity Starter', monthly: 4.99, annual: 39.99, inrRate: 400 },
    pro: { name: 'Align Pro', monthly: 9.99, annual: 79.99, inrRate: 800 },
    elite: { name: 'Elite Pioneer', monthly: 24.99, annual: 199.99, inrRate: 2000 },
  };

  const selectedPlan = plans[tier] || plans.pro;
  const priceUsd = billing === 'monthly' ? selectedPlan.monthly : selectedPlan.annual;
  const priceInr = billing === 'monthly' ? selectedPlan.inrRate : selectedPlan.inrRate * 10;
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Fetch Razorpay order ID
    axios.post(`${BACKEND_URL}/api/payment/razorpay-order`,
      { tier, billing_period: billing },
      { headers: { Authorization: `Bearer ${token}` } }
    )
    .then(res => {
      setOrderId(res.data.order_id);
    })
    .catch(err => {
      setError('Failed to create Razorpay order.');
    });
  }, [tier, billing, token, BACKEND_URL]);

  const handlePay = async (simulateSuccess = true) => {
    if (activeTab === 'upi' && !upiId) {
      setError('Please enter your UPI ID.');
      return;
    }
    if (activeTab === 'card' && (!cardNumber || !cardExpiry || !cardCvc)) {
      setError('Please fill in your card details.');
      return;
    }
    if (activeTab === 'netbanking' && !selectedBank) {
      setError('Please select your bank.');
      return;
    }

    setLoading(true);
    setError('');

    // Simulate payment authorization delay
    await new Promise(r => setTimeout(r, 2000));

    if (!simulateSuccess) {
      setError('Payment authorization failed. Razorpay error: BAD_REQUEST_PAYMENT_DECLINED');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/payment/razorpay-verify`, {
        razorpay_payment_id: `pay_test_${Math.random().toString(36).substring(2, 14)}`,
        razorpay_order_id: orderId,
        razorpay_signature: `sig_test_${Math.random().toString(36).substring(2, 20)}`,
        tier,
        billing_period: billing
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUser(response.data);
      setSuccess(true);

      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      setError('Razorpay signature verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const popularBanks = [
    { id: 'sbi', name: 'State Bank of India' },
    { id: 'hdfc', name: 'HDFC Bank' },
    { id: 'icici', name: 'ICICI Bank' },
    { id: 'axis', name: 'Axis Bank' },
  ];

  if (success) {
    return (
      <div className="min-h-screen bg-[#F0F3F7] flex flex-col items-center justify-center p-6 text-center">
        <CheckCircle className="h-16 w-16 text-emerald-500 mb-4 animate-bounce" />
        <h2 className="text-2xl font-bold text-neutral-800 mb-2">Payment Verification Successful</h2>
        <p className="text-sm text-neutral-500 mb-4">Redirecting you to Align Dashboard...</p>
        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4 relative antialiased font-sans">
      
      {/* Background Summary Container */}
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-lg border border-slate-200 p-8 space-y-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/checkout/payment-method?tier=${tier}&billing=${billing}`)}
            className="p-2 hover:bg-slate-50 rounded-full transition"
          >
            <ArrowLeft className="h-5 w-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-slate-800">Checkout Summary</h1>
            <p className="text-xs text-slate-400">Order ID: {orderId || 'Creating...'}</p>
          </div>
        </div>

        <div className="border-b border-slate-100 pb-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="font-semibold text-slate-800">{selectedPlan.name}</p>
              <p className="text-xs text-slate-400">Billing: {billing}</p>
            </div>
            <p className="font-bold text-slate-800">${priceUsd} USD</p>
          </div>
        </div>

        <div className="bg-slate-50 p-4 rounded-xl flex items-center justify-between text-xs text-slate-500">
          <span>Converted Amount (INR):</span>
          <span className="font-bold text-slate-700">₹{priceInr}.00</span>
        </div>

        <div className="text-center pt-8">
          <p className="text-xs text-slate-400">Loading Razorpay Secure Checkout...</p>
        </div>
      </div>

      {/* Razorpay overlay modal */}
      <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center p-4 backdrop-blur-xs">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[520px]">
          
          {/* Header */}
          <div className="bg-[#1C2D5A] text-white p-5 flex justify-between items-center">
            <div>
              <h3 className="font-bold text-base tracking-wide">Align OS</h3>
              <p className="text-xs text-slate-300 font-light">{selectedPlan.name} Subscription</p>
            </div>
            <div className="text-right">
              <h4 className="text-sm font-semibold">₹{priceInr}.00</h4>
              <p className="text-[10px] text-emerald-400 font-light">🔒 Secure Checkout</p>
            </div>
          </div>

          {/* Body Columns */}
          <div className="flex flex-1 overflow-hidden">
            
            {/* Sidebar Navigation */}
            <div className="w-1/3 bg-slate-50 border-r border-slate-100 flex flex-col text-xs font-semibold">
              <button
                onClick={() => { setActiveTab('upi'); setError(''); }}
                className={`p-4 border-b border-slate-100 text-left flex items-center gap-2 transition ${activeTab === 'upi' ? 'bg-white text-blue-600 border-l-4 border-blue-600' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                <Smartphone className="h-4 w-4" />
                <span>UPI / QR</span>
              </button>
              <button
                onClick={() => { setActiveTab('card'); setError(''); }}
                className={`p-4 border-b border-slate-100 text-left flex items-center gap-2 transition ${activeTab === 'card' ? 'bg-white text-blue-600 border-l-4 border-blue-600' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                <CreditCard className="h-4 w-4" />
                <span>Card</span>
              </button>
              <button
                onClick={() => { setActiveTab('netbanking'); setError(''); }}
                className={`p-4 border-b border-slate-100 text-left flex items-center gap-2 transition ${activeTab === 'netbanking' ? 'bg-white text-blue-600 border-l-4 border-blue-600' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                <Landmark className="h-4 w-4" />
                <span>Netbanking</span>
              </button>
            </div>

            {/* Content pane */}
            <div className="w-2/3 p-5 overflow-y-auto flex flex-col justify-between">
              <div>
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg mb-4 flex items-start gap-2">
                    <Info className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                {/* UPI Tab */}
                {activeTab === 'upi' && (
                  <div className="space-y-4">
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Pay using UPI</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-[10px] text-slate-500 mb-1">Enter VPA / UPI ID</label>
                        <input
                          type="text"
                          placeholder="username@upi"
                          value={upiId}
                          onChange={(e) => setUpiId(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:border-blue-500"
                        />
                      </div>
                      
                      <div className="border border-dashed border-slate-200 p-3 rounded-lg text-center flex flex-col items-center">
                        <span className="text-[10px] text-slate-400 mb-2">Or scan QR Code below</span>
                        <div className="w-28 h-28 bg-slate-100 border border-slate-200 flex items-center justify-center font-mono text-[8px] text-slate-400 p-2 text-center select-none leading-tight">
                          [ MOCK QR CODE: SCAN TO PAY FOR ALIGN OS ]
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Card Tab */}
                {activeTab === 'card' && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Credit or Debit Card</h4>
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Card Number"
                        value={cardNumber}
                        onChange={(e) => setCardNumber(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:border-blue-500"
                      />
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Expiry (MM/YY)"
                          value={cardExpiry}
                          onChange={(e) => setCardExpiry(e.target.value)}
                          className="w-1/2 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:border-blue-500"
                        />
                        <input
                          type="text"
                          placeholder="CVC"
                          value={cardCvc}
                          onChange={(e) => setCardCvc(e.target.value)}
                          className="w-1/2 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Netbanking Tab */}
                {activeTab === 'netbanking' && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Select Bank</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {popularBanks.map((bank) => (
                        <button
                          key={bank.id}
                          onClick={() => setSelectedBank(bank.id)}
                          className={`p-2.5 rounded-lg border text-center text-xs transition ${selectedBank === bank.id ? 'border-blue-500 bg-blue-50/30 text-blue-600 font-semibold' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                        >
                          {bank.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="pt-4 space-y-2">
                <button
                  onClick={() => handlePay(true)}
                  disabled={loading || !orderId}
                  className="w-full py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition active:scale-[0.98] flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Paying...</span>
                    </>
                  ) : (
                    <span>Pay ₹{priceInr}.00</span>
                  )}
                </button>
                <button
                  onClick={() => handlePay(false)}
                  disabled={loading || !orderId}
                  className="w-full py-2 border border-red-200 text-red-500 text-xs rounded-lg hover:bg-red-50/50 transition"
                >
                  Decline Transaction
                </button>
              </div>

            </div>

          </div>

          {/* Footer banner */}
          <div className="bg-slate-50 p-3 border-t border-slate-100 flex items-center justify-between text-[9px] text-slate-400">
            <span>Razorpay Trust Shield</span>
            <span>Support: support@razorpay.com</span>
          </div>

        </div>
      </div>

    </div>
  );
}
